/**
 * AgentInputBindings — 可视化配置 Dify 变量 → 平台用户字段绑定
 */
import React, { useCallback, useState } from 'react';
import { Alert, Button, Select, Space, Spin, Table, Typography } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import axios from '../../api';

const { Text } = Typography;

const EMPTY_OPTION = { value: '', label: '不绑定（学生手填）' };

const AgentInputBindings = ({ value = {}, onChange, apikey, agentId }) => {
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState([]);
  const [userFieldOptions, setUserFieldOptions] = useState([EMPTY_OPTION]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const applyBindings = useCallback((nextBindings) => {
    onChange?.(nextBindings || {});
  }, [onChange]);

  const handleLoadFromDify = async () => {
    if (!apikey && !agentId) {
      setError('请先填写 API-key');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = agentId
        ? { agent_id: agentId, api_key: apikey, input_bindings: value }
        : { api_key: apikey, input_bindings: value };
      const res = await axios.post('/api/dify/preview-parameters/', payload);
      const data = res.data?.data;
      if (!res.data?.success || !data) {
        throw new Error(res.data?.error || '加载失败');
      }

      const schema = data.form_schema || [];
      const options = [
        EMPTY_OPTION,
        ...(data.user_field_options || []).map((o) => ({ value: o.value, label: o.label })),
      ];
      setVariables(schema);
      setUserFieldOptions(options);
      setLoaded(true);

      const suggested = data.suggested_bindings || {};
      const merged = { ...value };
      schema.forEach((field) => {
        const key = field.key;
        if (!merged[key] && suggested[key]) {
          merged[key] = suggested[key];
        }
      });
      applyBindings(merged);
    } catch (err) {
      console.error('加载 Dify 变量失败:', err);
      setError(err.response?.data?.error || err.message || '加载失败');
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBindingChange = (difyKey, userField) => {
    const next = { ...value };
    if (userField) {
      next[difyKey] = userField;
    } else {
      delete next[difyKey];
    }
    applyBindings(next);
  };

  const columns = [
    {
      title: 'Dify 变量名',
      dataIndex: 'key',
      key: 'key',
      render: (text, record) => (
        <div>
          <Text code>{text}</Text>
          {record.label && record.label !== text && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.label}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: '绑定到平台用户字段',
      key: 'binding',
      render: (_, record) => (
        <Select
          style={{ width: '100%', minWidth: 200 }}
          placeholder="选择自动注入字段"
          allowClear
          value={value[record.key] || undefined}
          options={userFieldOptions}
          onChange={(v) => handleBindingChange(record.key, v)}
        />
      ),
    },
    {
      title: '说明',
      key: 'hint',
      render: (_, record) => {
        const bound = value[record.key];
        if (!bound) {
          return record.required
            ? <Text type="warning">学生需手填</Text>
            : <Text type="secondary">可选，学生手填</Text>;
        }
        const label = userFieldOptions.find((o) => o.value === bound)?.label || bound;
        return <Text type="success">自动注入：{label}</Text>;
      },
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="如何配置"
          description={
            <>
              1. 填写 API-key 后点击「从 Dify 加载变量」<br />
              2. 系统会列出 Dify 后台定义的变量名（如 student_id，建议绑定到「学号」）<br />
              3. 为需要自动带入的变量选择「用户 UID」等，<strong>不是写死某个学生的 ID</strong>
            </>
          }
        />
        <Button
          icon={<SyncOutlined />}
          onClick={handleLoadFromDify}
          loading={loading}
          disabled={!apikey && !agentId}
        >
          从 Dify 加载变量
        </Button>
        {error && <Alert type="error" message={error} showIcon />}
        {loading && <Spin tip="正在从 Dify 拉取变量..." />}
        {loaded && variables.length === 0 && (
          <Alert type="success" message="该智能体在 Dify 中没有需要学生填写的变量，可直接使用" showIcon />
        )}
        {loaded && variables.length > 0 && (
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            columns={columns}
            dataSource={variables}
          />
        )}
      </Space>
    </div>
  );
};

export default AgentInputBindings;
